import { useEffect, useState } from 'react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Button } from '@/components/ui/button';
import { activeEvents } from './projection';
import { durationLabel, laborAnchor, laborMetrics, numeric, scopedLaborEvents, tracerWindows } from './advancedLabor';
import type { ClinicalEvent, ClinicalSubject } from './types';

// Retained for consumers of the original chart projection.
export function laborChartPoints(events: ClinicalEvent[], subjectId: string, encounterId: string, eventType: string) {
  return activeEvents(events)
    .filter((e) => e.subject_id === subjectId && e.encounter_id === encounterId && e.event_type === eventType)
    .sort((a, b) => Date.parse(a.occurred_at) - Date.parse(b.occurred_at))
    .map((e) => ({ at: Date.parse(e.occurred_at), eventId: e.id, ...e.payload }));
}
const markerTypes = ['LABOR.FHR', 'VITALS.BP', 'VITALS.TEMP', 'ROM', 'MED.ADMIN', 'LABOR.CONTRACTIONS'];
function describe(e: ClinicalEvent) {
  const p = e.payload;
  if (e.event_type === 'LABOR.FHR') return `FHR ${p.low_bpm}–${p.high_bpm} bpm`;
  if (e.event_type === 'VITALS.BP') return `BP ${p.systolic}/${p.diastolic}`;
  if (e.event_type === 'VITALS.TEMP') return `Temperature ${p.value_c ?? '—'} °C`;
  if (e.event_type === 'MED.ADMIN') return `${p.drug ?? 'Medication'} · ${p.status ?? 'status not documented'}`;
  if (e.event_type === 'LABOR.CONTRACTIONS')
    return `Contractions · ${p.duration_s_low ?? '—'}–${p.duration_s_high ?? p.duration_s_low ?? '—'} s`;
  return 'Rupture of membranes';
}
function Axis({ start, end, anchor, timeZone }: { start: number; end: number; anchor?: number; timeZone: string }) {
  return (
    <>
      {[0, 0.25, 0.5, 0.75, 1].map((f) => (
        <text key={f} x={55 + f * 690} y={226} textAnchor="middle" fontSize="11" fill="currentColor">
          {anchor !== undefined
            ? `${((start + f * (end - start) - anchor) / 3600000).toFixed(1)}h`
            : new Date(start + f * (end - start)).toLocaleTimeString(undefined, {
                timeZone,
                hour: '2-digit',
                minute: '2-digit',
              })}
        </text>
      ))}
    </>
  );
}
export function TapTracerBars({
  samples,
}: {
  samples: { start: number; end: number; bpm: number; contraction: boolean }[];
}) {
  if (!samples.length) return null;
  const end = samples.at(-1)!.end;
  return (
    <figure className="min-w-0">
      <svg
        role="img"
        aria-label="Attended tap-count samples in bpm; shaded windows include a contraction marker"
        viewBox="0 0 800 190"
        className="w-full">
        {[0, 100, 200, 300].map((n) => (
          <g key={n}>
            <line x1="45" x2="785" y1={160 - n * 0.45} y2={160 - n * 0.45} stroke="currentColor" opacity=".15" />
            <text x="40" y={164 - n * 0.45} textAnchor="end" fill="currentColor" fontSize="11">
              {n}
            </text>
          </g>
        ))}
        {samples.map((s, i) => (
          <g key={i}>
            <title>
              {(s.start / 1000).toFixed(1)}–{(s.end / 1000).toFixed(1)} seconds: {s.bpm} bpm
              {s.contraction ? ', contraction marked during window' : ''}
            </title>
            {s.contraction && (
              <rect
                x={45 + (s.start / end) * 740}
                y="15"
                width={((s.end - s.start) / end) * 740}
                height="145"
                fill="var(--chart-2)"
                opacity=".2"
              />
            )}
            <rect
              x={45 + (s.start / end) * 740 + 1}
              y={160 - s.bpm * 0.45}
              width={Math.max(1, ((s.end - s.start) / end) * 740 - 2)}
              height={s.bpm * 0.45}
              fill="var(--chart-1)"
            />
          </g>
        ))}
        <text x="45" y="182" fill="currentColor" fontSize="11">
          0 s
        </text>
        <text x="785" y="182" textAnchor="end" fill="currentColor" fontSize="11">
          {(end / 1000).toFixed(1)} s
        </text>
      </svg>
      <figcaption className="text-sm text-muted-foreground">
        Shading marks a contraction during that observation window; it does not establish exact contraction onset or
        duration.
      </figcaption>
    </figure>
  );
}
export function LaborCharts({
  events,
  subjects,
  episodeId,
  encounterId,
  timeZone,
  onEdit,
}: {
  events: ClinicalEvent[];
  subjects: ClinicalSubject[];
  episodeId: string;
  encounterId: string;
  timeZone: string;
  onEdit?: (event: ClinicalEvent) => void;
}) {
  const [now, setNow] = useState(Date.now());
  const [origin, setOrigin] = useState<'ADMITTED' | 'ACTIVE_LABOR'>('ADMITTED');
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 10000);
    return () => clearInterval(id);
  }, []);
  const rows = scopedLaborEvents(events, episodeId, encounterId, now);
  const mother = subjects.find((s) => s.subject_type === 'MOTHER')?.id ?? '';
  const anchorEvent = laborAnchor(rows, mother, origin),
    anchor = anchorEvent ? Date.parse(anchorEvent.occurred_at) : undefined;
  const progress = rows.filter((e) => e.subject_id === mother && e.event_type === 'LABOR.PROGRESS');
  const relevant = rows.filter(
    (e) => e.subject_id === mother || subjects.some((s) => s.subject_type === 'FETUS' && s.id === e.subject_id),
  );
  const start = Math.min(anchor ?? now, ...relevant.map((e) => Date.parse(e.occurred_at)));
  const end = Math.max(
    start + 60000,
    ...relevant.map((e) => Date.parse(e.occurred_at) + (tracerWindows(e).at(-1)?.end ?? 0)),
  );
  const x = (at: number) => 55 + ((at - start) / (end - start)) * 690;
  const time = (at: string) => new Date(at).toLocaleString(undefined, { timeZone });
  return (
    <details>
      <summary className="min-h-11 cursor-pointer py-3">Labor curves, FHR strip and status metrics</summary>
      <div className="flex min-w-0 flex-col gap-4">
        <h4 className="font-semibold">Labor progress</h4>
        <ToggleGroup
          type="single"
          value={origin}
          onValueChange={(v) => {
            if (v) setOrigin(v as typeof origin);
          }}
          aria-label="Labor curve time origin">
          <ToggleGroupItem value="ADMITTED" className="min-h-11">
            Since admission
          </ToggleGroupItem>
          <ToggleGroupItem value="ACTIVE_LABOR" className="min-h-11">
            Since active labor
          </ToggleGroupItem>
        </ToggleGroup>
        {anchor === undefined && (
          <p>The selected start milestone is not documented in this encounter. Clock times are shown.</p>
        )}
        <div className="overflow-x-auto">
          <svg
            role="img"
            aria-label="Observed cervical dilation and station over time"
            viewBox="0 0 800 245"
            className="min-w-[540px] w-full">
            {[0, 2, 4, 6, 8, 10].map((n) => (
              <g key={n}>
                <line x1="55" x2="745" y1={200 - n * 17} y2={200 - n * 17} stroke="currentColor" opacity=".15" />
                <text x="48" y={204 - n * 17} textAnchor="end" fill="currentColor" fontSize="11">
                  {n}
                </text>
                <text x="752" y={204 - n * 17} fill="currentColor" fontSize="11">
                  {n - 5}
                </text>
              </g>
            ))}
            <text x="55" y="15" fill="var(--chart-1)" fontSize="12">
              Dilation (cm)
            </text>
            <text x="745" y="15" textAnchor="end" fill="var(--chart-2)" fontSize="12">
              Station
            </text>
            {(['dilation_cm', 'station'] as const).map((key, i) => {
              const points = progress.filter((e) => numeric(e.payload[key]));
              return (
                <g key={key}>
                  <polyline
                    fill="none"
                    stroke={`var(--chart-${i + 1})`}
                    strokeDasharray={i ? '5 4' : undefined}
                    points={points
                      .map(
                        (e) =>
                          `${x(Date.parse(e.occurred_at))},${200 - ((e.payload[key] as number) + (i ? 5 : 0)) * 17}`,
                      )
                      .join(' ')}
                  />
                  {points.map((e) => (
                    <circle
                      key={e.id}
                      cx={x(Date.parse(e.occurred_at))}
                      cy={200 - ((e.payload[key] as number) + (i ? 5 : 0)) * 17}
                      r="4"
                      fill={`var(--chart-${i + 1})`}>
                      <title>
                        {time(e.occurred_at)}: {key === 'station' ? 'Station' : 'Dilation'} {String(e.payload[key])}
                      </title>
                    </circle>
                  ))}
                </g>
              );
            })}
            <Axis start={start} end={end} anchor={anchor} timeZone={timeZone} />
          </svg>
        </div>
        <p className="text-sm text-muted-foreground">
          Observed values only. Connecting lines are visual guides, not continuous measurements. No reference curve or
          diagnosis of labor arrest is applied.
        </p>
        {subjects
          .filter((s) => s.subject_type === 'FETUS')
          .map((f) => {
            const fhr = rows.filter((e) => e.subject_id === f.id && e.event_type === 'LABOR.FHR');
            const m = laborMetrics(rows, mother, f.id, now);
            const metrics = [
              ['Total labor', m.totalLabor],
              ['Since active labor', m.activeLabor],
              ['Since ROM', m.sinceRom],
              ['Pushing', m.pushing],
              ['Since last FHR', m.lastFhr],
              ['Since last maternal vital sign', m.lastVitals],
              ['Since last documented cervical change', m.cervicalChange],
            ] as const;
            return (
              <section key={f.id} className="flex min-w-0 flex-col gap-3">
                <h4 className="font-semibold">{f.label} — FHR strip and labor status</h4>
                <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {metrics.map(([label, value]) => (
                    <div key={label}>
                      <dt className="text-sm text-muted-foreground">{label}</dt>
                      <dd>{durationLabel(value)}</dd>
                    </div>
                  ))}
                  {(['intake', 'output'] as const).map((key) => (
                    <div key={key}>
                      <dt className="text-sm text-muted-foreground">Documented maternal fluid {key}</dt>
                      <dd>
                        {m[key].value === null ? 'Not quantified' : `${m[key].value} mL`}
                        {m[key].unquantified > 0 ? ` · ${m[key].unquantified} unquantified entries` : ''}
                      </dd>
                    </div>
                  ))}
                </dl>
                <p className="text-sm text-muted-foreground">
                  Current encounter only.{' '}
                  {m.born
                    ? 'Labor, ROM and pushing durations stop at this fetus’s documented birth.'
                    : 'Durations run to the current time.'}{' '}
                  Missing observations remain unknown. A vital-sign timestamp does not mean a complete assessment.
                  Totals include only documented numeric amounts.
                </p>
                {fhr.length ? (
                  <div className="overflow-x-auto">
                    <svg
                      role="img"
                      aria-label={`${f.label} intermittent auscultation ranges; observations are not a continuous fetal monitor`}
                      viewBox="0 0 800 245"
                      className="min-w-[540px] w-full">
                      {[0, 60, 120, 180, 240, 300].map((n) => (
                        <g key={n}>
                          <line
                            x1="55"
                            x2="745"
                            y1={200 - n * 0.56}
                            y2={200 - n * 0.56}
                            stroke="currentColor"
                            opacity=".15"
                          />
                          <text x="48" y={204 - n * 0.56} textAnchor="end" fill="currentColor" fontSize="11">
                            {n}
                          </text>
                        </g>
                      ))}
                      {fhr.map((e) => {
                        const px = x(Date.parse(e.occurred_at));
                        return (
                          <g key={e.id}>
                            <title>
                              {time(e.occurred_at)} · {describe(e)}
                            </title>
                            {tracerWindows(e)
                              .filter((s) => s.contraction)
                              .map((s, i) => (
                                <rect
                                  key={i}
                                  x={x(Date.parse(e.occurred_at) + s.start)}
                                  y="25"
                                  width={Math.max(1, ((s.end - s.start) / (end - start)) * 690)}
                                  height="175"
                                  fill="var(--chart-2)"
                                  opacity=".2"
                                />
                              ))}
                            {numeric(e.payload.low_bpm) && numeric(e.payload.high_bpm) && (
                              <>
                                <line
                                  x1={px}
                                  x2={px}
                                  y1={200 - e.payload.low_bpm * 0.56}
                                  y2={200 - e.payload.high_bpm * 0.56}
                                  stroke="var(--chart-1)"
                                  strokeWidth="4"
                                />
                                <circle cx={px} cy={200 - e.payload.low_bpm * 0.56} r="3" fill="var(--chart-1)" />
                                <circle cx={px} cy={200 - e.payload.high_bpm * 0.56} r="3" fill="var(--chart-1)" />
                              </>
                            )}
                            {numeric(e.payload.baseline_bpm) && (
                              <circle cx={px} cy={200 - e.payload.baseline_bpm * 0.56} r="4" fill="var(--chart-3)" />
                            )}
                          </g>
                        );
                      })}
                      <Axis start={start} end={end} anchor={anchor} timeZone={timeZone} />
                    </svg>
                  </div>
                ) : (
                  <p>No confirmed FHR observations in this encounter.</p>
                )}
                <p className="text-sm text-muted-foreground">
                  Vertical bars show observed low–high bpm; separate dots show a clinician-entered baseline. Shading
                  marks tap windows with a contraction. Summary contraction entries do not establish timed bands.
                </p>
                {fhr
                  .filter((e) => tracerWindows(e).length)
                  .map((e) => (
                    <details key={e.id}>
                      <summary className="min-h-11 cursor-pointer py-3">Tap tracer · {time(e.occurred_at)}</summary>
                      <TapTracerBars samples={tracerWindows(e)} />
                    </details>
                  ))}
              </section>
            );
          })}
        <h4 className="font-semibold">Maternal and fetal markers</h4>
        <div className="overflow-x-auto">
          <svg
            role="img"
            aria-label="Time-aligned FHR, blood pressure, temperature, membrane rupture, medications and contraction observations"
            viewBox="0 0 800 245"
            className="min-w-[540px] w-full">
            {markerTypes.map((type, i) => (
              <g key={type}>
                <text x="55" y={25 + i * 30} fill="currentColor" fontSize="10">
                  {['FHR', 'BP', 'Temp', 'ROM', 'Medication', 'Contractions'][i]}
                </text>
                <line x1="120" x2="745" y1={30 + i * 30} y2={30 + i * 30} stroke="currentColor" opacity=".15" />
                {relevant
                  .filter((e) => e.event_type === type)
                  .map((e) => (
                    <circle
                      key={e.id}
                      cx={120 + ((Date.parse(e.occurred_at) - start) / (end - start)) * 625}
                      cy={30 + i * 30}
                      r="4"
                      fill="var(--chart-1)">
                      <title>
                        {time(e.occurred_at)} · {describe(e)}
                      </title>
                    </circle>
                  ))}
              </g>
            ))}
            {[0, 0.25, 0.5, 0.75, 1].map((f) => (
              <text key={f} x={120 + f * 625} y="220" textAnchor="middle" fill="currentColor" fontSize="11">
                {anchor !== undefined
                  ? `${((start + f * (end - start) - anchor) / 3600000).toFixed(1)}h`
                  : new Date(start + f * (end - start)).toLocaleTimeString(undefined, {
                      timeZone,
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
              </text>
            ))}
          </svg>
        </div>
        <p className="text-sm text-muted-foreground">
          Time-aligned observations and interventions; select an entry to inspect its source.
        </p>
        <ol className="flex flex-col gap-2">
          {relevant
            .filter((e) => markerTypes.includes(e.event_type) || e.event_type === 'LABOR.PROGRESS')
            .map((e) => (
              <li key={e.id} className="flex flex-wrap items-center gap-2">
                <time dateTime={e.occurred_at}>{time(e.occurred_at)}</time>
                {anchor !== undefined && <span>({((Date.parse(e.occurred_at) - anchor) / 60000).toFixed(0)} min)</span>}
                <span>
                  {subjects.find((s) => s.id === e.subject_id)?.label} ·{' '}
                  {e.event_type === 'LABOR.PROGRESS'
                    ? `Dilation ${e.payload.dilation_cm ?? '—'} cm · station ${e.payload.station ?? '—'}`
                    : describe(e)}
                </span>
                {onEdit && (
                  <Button type="button" variant="outline" className="min-h-11" onClick={() => onEdit(e)}>
                    Review source
                  </Button>
                )}
              </li>
            ))}
        </ol>
      </div>
    </details>
  );
}
